import { HawksearchGlobal } from '../models';
import defaultStylesheet from '../scss/hawksearch.scss';

declare let Hawksearch: HawksearchGlobal;

export abstract class BaseComponent<TData> extends HTMLElement {
    protected handlebars = Hawksearch.handlebars;

    protected abstract bindEvent: string | undefined;
    protected abstract customTemplatePropertyName: string | undefined;
    protected abstract defaultHtml: string;

    data?: TData;

    get hasCustomTemplate(): boolean {
        return !!this.innerHTML.trim().length;
    }

    private bindEventHandler?: EventListener;
    private template!: HandlebarsTemplateDelegate;

    connectedCallback(): void {
        this.attachShadow({
            mode: 'open'
        });

        this.registerHelpers();

        const customTemplateHtml = this.getCustomTemplateHtml();
        const templateHtml = customTemplateHtml ?? this.defaultHtml;

        this.template = this.handlebars.compile(templateHtml);

        setTimeout(() => {
            this.renderContent();
        });

        if (this.bindEvent) {
            this.bindEventHandler = ((event: CustomEvent): void => {
                this.data = event.detail;

                this.renderContent();
            }) as EventListener;

            addEventListener(this.bindEvent, this.bindEventHandler);
        }
    }

    disconnectedCallback(): void {
        if (this.bindEvent && this.bindEventHandler) {
            removeEventListener(this.bindEvent, this.bindEventHandler);
        }
    }

    protected registerHelpers(): void {}

    public renderContent(): void {
        if (!this.template) {
            return;
        }

        const elements: Array<Node> = [];
        let stylesheets: Array<string> = [];

        if (Hawksearch.config.css?.defaultStyles !== false) {
            stylesheets.push(defaultStylesheet);
        }

        if (Hawksearch.config.css?.customStyles) {
            const customStylesheets =
                typeof Hawksearch.config.css.customStyles === 'string' ? [Hawksearch.config.css.customStyles] : Hawksearch.config.css.customStyles;

            stylesheets = stylesheets.concat(customStylesheets);
        }

        stylesheets.forEach((url) => {
            const customStyles = document.createElement('link');

            customStyles.type = 'text/css';
            customStyles.rel = 'stylesheet';
            customStyles.href = url;

            elements.push(customStyles);
        });

        const templateElement = document.createElement('template');

        templateElement.innerHTML = this.getContentHtml(this.template);

        this.style.display = !templateElement.innerHTML ? 'none' : '';

        const content = templateElement.content.cloneNode(true);

        elements.push(content);

        this.shadowRoot?.replaceChildren(...elements);

        this.bindChildElements();
    }

    protected abstract getContentHtml(template: HandlebarsTemplateDelegate): string;

    protected bindChildElements(): void {}

    private getCustomTemplateHtml(): string | undefined {
        let customTemplate: string | undefined;

        if (this.hasCustomTemplate) {
            customTemplate = this.innerHTML;
        } else {
            if (this.customTemplatePropertyName) {
                customTemplate = (Hawksearch.config.templates as any)?.[this.customTemplatePropertyName];
            }

            if (customTemplate) {
                const customTemplateElement = document.getElementById(customTemplate);

                if (customTemplateElement instanceof HTMLTemplateElement) {
                    customTemplate = customTemplateElement.innerHTML;
                }

                const replacements = {
                    '{{&gt;': '{{>'
                };

                Object.entries(replacements).forEach(([input, output]) => {
                    const regex = new RegExp(input, 'g');

                    customTemplate = customTemplate!.replace(regex, output);
                });
            }
        }

        return customTemplate;
    }
}
